"use client"

import { useState } from "react"
import { router } from '@inertiajs/react'
import DashboardLayout from '@/layout/dashboard-layout'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { Checkbox } from "@/components/ui/checkbox"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Calendar, Clock, Edit, Trash2, Search, Play } from "lucide-react"
import { toast } from "sonner"

type ScheduledPost = {
  id: number;
  title: string;
  blog: string;
  scheduled_date: string;
  scheduled_time: string;
  scheduled_at: string;
  status: string;
  category: string;
  content_preview: string;
};

type ScheduledPostsProps = {
  posts: ScheduledPost[];
  filters: {
    blogs: string[];
    categories: string[];
  };
  stats: {
    total: number;
    today: number;
    this_week: number;
  };
};

export default function ScheduledPosts({ posts, filters, stats }: ScheduledPostsProps) {
  console.log('🔍 ScheduledPosts - Dados recebidos:', { posts, filters, stats });
  
  // Fallback para dados undefined - CORRIGE O ERRO DE FILTER
  const safePosts = posts || [];
  const safeFilters = filters || { blogs: ['all'], categories: ['all'] };
  const safeStats = stats || { total: 0, today: 0, this_week: 0 };
  
  const [selectedPosts, setSelectedPosts] = useState<number[]>([])
  const [searchTerm, setSearchTerm] = useState("")
  const [filterBlog, setFilterBlog] = useState("all")
  const [filterStatus, setFilterStatus] = useState("all")
  const [deleteConfirm, setDeleteConfirm] = useState<{ show: boolean; postId: number | null; postTitle: string }>({ show: false, postId: null, postTitle: '' })

  const handleSelectPost = (postId: number, checked: boolean) => {
    if (checked) {
      setSelectedPosts([...selectedPosts, postId])
    } else {
      setSelectedPosts(selectedPosts.filter((id) => id !== postId))
    }
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "agendado":
        return <Badge variant="default">Agendado</Badge>
      case "processando":
        return <Badge variant="secondary">Processando</Badge>
      case "erro":
        return <Badge variant="destructive">Erro</Badge>
      default:
        return <Badge variant="outline">Desconhecido</Badge>
    }
  }

  const handleExecuteNow = async (postId: number) => {
    try {
      const response = await fetch(`/dashboard/posts/${postId}/execute_now`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        }
      })
      
      const data = await response.json()
      
      if (data.success) {
        toast.success(data.message)
        router.reload()
      } else {
        toast.error(data.message)
      }
    } catch (error) {
      toast.error('Erro ao executar post')
      console.error('Erro:', error)
    }
  }

  const handleEdit = (postId: number) => {
    router.visit(`/dashboard/posts/${postId}/edit`)
  }

  const handleDelete = (postId: number) => {
    console.log('=== DEBUG HANDLE DELETE ====')
    console.log('Post ID:', postId)
    const post = safePosts.find(p => p.id === postId)
    console.log('Post encontrado:', post)
    setDeleteConfirm({ 
      show: true, 
      postId, 
      postTitle: post?.title || 'Post' 
    })
    console.log('Modal de confirmação aberto')
  }

  const confirmDelete = () => {
    console.log('=== DEBUG CONFIRM DELETE ====')
    console.log('Delete confirm state:', deleteConfirm)
    
    if (!deleteConfirm.postId) {
      console.log('Nenhum post ID encontrado, retornando')
      return
    }
    
    console.log('Enviando requisição POST para destroy_post:', `/dashboard/posts/${deleteConfirm.postId}/destroy_post`)
    
    router.post(`/dashboard/posts/${deleteConfirm.postId}/destroy_post`, {}, {
      onSuccess: () => {
        console.log('DELETE bem-sucedido!')
        setDeleteConfirm({ show: false, postId: null, postTitle: '' })
        // Forçar reload da página para atualizar a lista
        router.reload()
      },
      onError: (errors: any) => {
        console.log('Erro no DELETE:', errors)
        console.error('Erro ao excluir post:', errors)
        toast.error('Erro ao excluir post')
        setDeleteConfirm({ show: false, postId: null, postTitle: '' })
      }
    })
  }

  const cancelDelete = () => {
    setDeleteConfirm({ show: false, postId: null, postTitle: '' })
  }

  // USAR safePosts ao invés de posts para evitar erro de filter
  const filteredPosts = safePosts.filter((post) => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesBlog = filterBlog === "all" || post.blog === filterBlog
    const matchesStatus = filterStatus === "all" || post.status === filterStatus
    return matchesSearch && matchesBlog && matchesStatus
  })

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedPosts(filteredPosts.map((post) => post.id))
    } else {
      setSelectedPosts([])
    }
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Postagens Agendadas</h1>
          <p className="text-muted-foreground">Gerencie suas postagens programadas para publicação</p>
        </div>

        {/* Filtros e Busca */}
        <Card>
          <CardHeader>
            <CardTitle>Filtros</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Buscar por título..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-8"
                  />
                </div>
              </div>
              <Select value={filterBlog} onValueChange={setFilterBlog}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filtrar por blog" />
                </SelectTrigger>
                <SelectContent>
                  {safeFilters.blogs.map((blog) => (
                    <SelectItem key={blog} value={blog}>
                      {blog === "all" ? "Todos os blogs" : blog}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filtrar por status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os status</SelectItem>
                  <SelectItem value="agendado">Agendado</SelectItem>
                  <SelectItem value="processando">Processando</SelectItem>
                  <SelectItem value="erro">Erro</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Ações em Massa */}
        {selectedPosts.length > 0 && (
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">{selectedPosts.length} postagem(ns) selecionada(s)</p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm">
                    <Calendar className="mr-2 h-4 w-4" />
                    Reprogramar
                  </Button>
                  <Button variant="destructive" size="sm">
                    <Trash2 className="mr-2 h-4 w-4" />
                    Excluir
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabela de Postagens */}
        <Card>
          <CardHeader>
            <CardTitle>Lista de Postagens Agendadas</CardTitle>
            <CardDescription>{filteredPosts.length} postagem(ns) encontrada(s)</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox 
                      checked={selectedPosts.length === filteredPosts.length && filteredPosts.length > 0} 
                      onCheckedChange={handleSelectAll} 
                    />
                  </TableHead>
                  <TableHead>Título</TableHead>
                  <TableHead>Blog</TableHead>
                  <TableHead>Data/Hora</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Categoria</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPosts.map((post) => (
                  <TableRow key={post.id}>
                    <TableCell>
                      <Checkbox
                        checked={selectedPosts.includes(post.id)}
                        onCheckedChange={(checked) => handleSelectPost(post.id, checked as boolean)}
                      />
                    </TableCell>
                    <TableCell className="font-medium">{post.title}</TableCell>
                    <TableCell>{post.blog}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        {post.scheduled_date}
                        <Clock className="h-4 w-4 text-muted-foreground ml-2" />
                        {post.scheduled_time}
                      </div>
                    </TableCell>
                    <TableCell>{getStatusBadge(post.status)}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{post.category}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleExecuteNow(post.id)}
                          title="Executar agora"
                          className="hover:bg-green-50 hover:text-green-600"
                        >
                          <Play className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleEdit(post.id)}
                          title="Editar"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleDelete(post.id)}
                          title="Excluir"
                          className="hover:bg-red-50 hover:text-red-600"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      {/* Modal de Confirmação de Exclusão */}
      <Dialog open={deleteConfirm.show} onOpenChange={(open) => !open && cancelDelete()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Exclusão</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja excluir o post <strong>"{deleteConfirm.postTitle}"</strong>?
              <br />
              Esta ação não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={cancelDelete}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={confirmDelete}>
              Excluir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  )
}
